import {ControlType} from '../forms/item/item.model';

// export const DECIMAL_PATTERN = '^-?[0-9]\\d*([\\.\\,]\\d*)?$';
export const DECIMAL_PATTERN = '^-?[0-9]\\d*([\\.]\\d*)?$';  // without ,
export const INTEGER_PATTERN = '^\\d+$';

export abstract class MasterData {
  uuid: string;
  name: string;
  externalId: string;
  translations?: Record<string, string>;
}

// language.model.ts
export class Language {
  id: number;
  name: string;
  locale: string;
  asDefault: boolean;
  enabled: boolean;
  mapCenterLatitude: number | null;
  mapCenterLongitude: number | null;

  constructor(data?: Partial<Language>) {
    this.id = data?.id ?? 0;
    this.name = data?.name ?? '';
    this.locale = data?.locale ?? '';
    this.asDefault = data?.asDefault ?? false;
    this.enabled = data?.enabled ?? false;
    this.mapCenterLatitude = data?.mapCenterLatitude ?? null;
    this.mapCenterLongitude = data?.mapCenterLongitude ?? null;
  }
}


export class Area implements MasterData {
  uuid: string;
  name: string;
  externalId: string;
}

export class Attribute implements MasterData {
  uuid: string;
  name: string;
  externalId: string;
  renderTag: string;
  type: AttributeType;
}

export enum AttributeType {
  USER = 0,
  INSTALLATION = 1
}

export class Branch {
  uuid: string;
  name?: string;
  externalId?: string;
  areas?: Area[];
  fullname?: string;
  address?: string;
  city?: string;
  postal?: string;
  phone?: string;
  email?: string;
  contact?: string;
}

export class Priority implements MasterData {
  uuid: string;
  name: string;
  externalId: string;
  metaId: string;
}

export class Department implements MasterData {
  uuid: string;
  name: string;
  externalId: string;

  toMap(department: Department): Map<string, string | boolean> {
    const map = new Map<string, string | boolean>();
    map.set('uuid', department.uuid);
    map.set('name', department.name);
    map.set('externalId', department.externalId);
    return map;
  }
}

export class FormType implements MasterData {
  uuid: string;
  name: string;
  externalId: string;

  toMap(ftype: FormType): Map<string, string | boolean> {
    const map = new Map<string, string | boolean>();
    map.set('uuid', ftype.uuid);
    map.set('name', ftype.name);
    map.set('externalId', ftype.externalId);
    return map;
  }
}

export enum OrderState {
  NEW = 0,
  PENDING = 1,
  ASSIGNED = 2,
  AT_WORK = 3,
  PAUSED = 4,
  STOPPED = 5,
  POSTPROCESSING = 6,
  ENDED = 7,
}

export class Status implements MasterData {
  uuid: string;
  name: string;
  externalId: string;
  metaId: number;
  allowedStatuses?: Status[];
  orderState?: OrderState;
  icon: StatusIcon;
  iconColor: string;


  toMap(status: Status): Map<string, string | boolean | unknown> {
    const map = new Map<string, string | boolean | unknown>();
    map.set('uuid', status.uuid);
    map.set('name', status.name);
    map.set('externalId', status.externalId);
    map.set('metaId', status.metaId);
    map.set('orderState', status.orderState);
    map.set('icon', status.icon);
    map.set('iconColor', status.iconColor);
    if (status.allowedStatuses?.length > 0) {
      const stats = new Map<string, string | boolean | unknown>();
      status.allowedStatuses.forEach((s) => {
        const m = new Map<string, string | boolean | unknown>();
        m.set('uuid', s.uuid);
        m.set('name', s.name);
        m.set('externalId', s.externalId);
        m.set('metaId', s.metaId);
        m.set('orderState', status.orderState);
        stats.set(s.uuid, m);
      });
    }
    return map;
  }
}

export class QuestionnaireField {
  tag: string;
  title: string;
  type: ControlType;
  values: DropdownValue[];
  customer: Customer;
  status: Status;
  required: boolean;
  sort?: number = 0;
  uuid: string;


  constructor(tag: string, title: string, type: ControlType, values: DropdownValue[]
    , customer: Customer, status: Status, required: boolean, sort: number) {
    this.tag = tag;
    this.title = title;
    this.type = type;
    this.values = values;
    this.customer = customer;
    this.status = status;
    this.required = required;
    this.sort = sort;
  }
}

export class DropdownValue {
  title: string;
  value: string;
  uuid: string;

  constructor(title: string, value: string) {
    this.title = title;
    this.value = value;
  }
}

export enum ProductAttributeType {
  BOOLEAN = 0,
  TEXT = 3,
  INTEGER = 4,
  DECIMAL = 5,
  TIMESTAMP = 12
}

export class ProductCategoryAttribute {
  name: string;
  key: string;
  sort: number;
  productAttributeType: ProductAttributeType;

  constructor(name: string, key: string, sort: number, prAttr: ProductAttributeType) {
    this.name = name;
    this.key = key;
    this.sort = sort;
    this.productAttributeType = prAttr;
  }
}

export class ProductCategory implements MasterData {
  uuid: string;
  name: string;
  externalId: string;
  productCategoryAttributes: ProductCategoryAttribute[];
}

export class StatusIcon implements MasterData {
  name: string;
  externalId: string;
  uuid: string;
}

export class Customer {
  name?: string;
  address?: string;
  city?: string;
  postal?: string;
  nip?: string;
  uuid?: string;
  externalId?: string;
  sendFormByEmail?: boolean;
  formEmailAddress?: string;
}

export interface CustomerSearchAPI {
  content: Customer[];
  pageable: Pageable;
  empty: boolean;
  first: boolean;
  last: boolean;
  number: number;
  numberOfElements: number;
  size: number;
  sort: SortAPI;
  totalElements: number;
  totalPages: number;

}

export class SortAPI {
  sorted: boolean;
  unsorted: boolean;
  empty: boolean;
}

export class Pageable {
  sort: SortAPI;
  empty: boolean;
  first: boolean;
  last: boolean;
  number: number;
  unpaged: boolean;
}
export class Installation {
  uuid?: string;
  name?: string;
  address?: string;
  city?: string;
  postal?: string;
  areaUuid?: string;
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
  customerUuid?: string;
  customerName?: string;
  longitude?: number;
  latitude?: number;
  externalId?: string;
  sendFormByEmail?: boolean;
  formEmailAddress?: string;
  base64Picture?: string;
  attributes?: AttributeValue[];
  departments?: Array<Department>;
  devices?: Array<Device>;
}

export class AttributeValue {
  uuid: string;
  attribute: Attribute;
  value: string;
  externalId?: string;

  constructor(attr: Attribute, value: string) {
    this.attribute = attr;
    this.value = value;
  }
}

export class Device {
  uuid: string;
  externalId?: string;
  installation: Installation;
  serial: string;
  product: Product;
  productUuid: string;
  productModel: string;
  productCategoryUuid: string;
  remarks: string;
  fitter: string;
  installationDate: string;
  location?: string;
}

export class Product {
  uuid: string;
  externalId?: string;
  model: string;
  catalogNo: string;
  productCategoryUuid: string;
  productAttributes: ProductAttribute[];

}

export class ProductAttribute {
  value: string;
  productCategoryAttributeUuid: string;

  constructor(prodCategAttrUuid: string, value: any) {
    this.productCategoryAttributeUuid = prodCategAttrUuid;
    this.value = value.toString();
  }
}

export class OrderType {
  name: string;
  uuid: string;
  externalId?: string;
}

